import React from 'react';

import { useIntl } from 'ha/i18n';
import { defineKey } from '@ha/intl';
import { Grid, Typography } from '@hbf/dsl';
import { isContentfulSupported } from 'ha/modules/Hermes/utils/isContentfulSupported';
import { useFeatureFlags, isEnabled } from 'ha/modules/FeatureFlags';
import { hasLandlordsCommunity } from 'ha/modules/Hermes/utils/hasLandlordsCommunity';
import { NOFOLLOW_REF } from '../constants';
import { useStyles } from './LinkList';
import { LinkItem } from './LinkItem';

export interface Props {
  rel: string;
}

const LandlordsLinks: React.FC<Props> = ({ rel }) => {
  const { T, urlResolver, lang } = useIntl();
  const { classes } = useStyles();
  const { showPrivateLandlordLandingPageItaly, enableBlogHomepageLandlord } =
    useFeatureFlags();

  const isBlogEnabled = React.useMemo(
    () => isEnabled(enableBlogHomepageLandlord),
    [enableBlogHomepageLandlord],
  );

  return (
    <Grid item xs={1} data-test-locator="Footer/Landlords">
      <Typography.ParagraphTitle className={classes.columnTitle} color="white">
        {T('Landlords')}
      </Typography.ParagraphTitle>
      {!isBlogEnabled && hasLandlordsCommunity(lang) && (
        <LinkItem
          track={{ name: 'Landlord community' }}
          link={{ to: urlResolver.getLandlordsCommunityUrl(lang), rel }}
          label={defineKey('Blog for landlords')}
        />
      )}

      <LinkItem
        track={{ name: 'How to rent out' }}
        link={{ to: urlResolver.getRentingOut(), rel }}
        label={defineKey('How to rent out')}
      />

      <LinkItem
        track={{ name: 'Pricing landlords' }}
        link={{ to: urlResolver.getPricingLandlordsUrl(), rel }}
        label={defineKey('Pricing')}
      />

      {isContentfulSupported(lang) && (
        <LinkItem
          track={{ name: 'Become an advertiser' }}
          link={{
            to: urlResolver.getPrivateLandlordLandingPageUrl({
              showPrivateLandlordLandingPageItaly:
                showPrivateLandlordLandingPageItaly === 'on',
            }),
          }}
          label={defineKey('Become an advertiser')}
        />
      )}

      {isBlogEnabled && urlResolver.hasLandlordsBlogUrl(lang) && (
        <LinkItem
          track={{ name: 'Blog for landlords' }}
          link={{ to: urlResolver.getLandlordsBlogUrl(lang), rel }}
          label={defineKey('Blog for landlords')}
        />
      )}

      <LinkItem
        track={{ name: 'RentRadar' }}
        link={{
          to: urlResolver.getRentRadarUrl(),
          rel: NOFOLLOW_REF,
          target: '_blank',
        }}
        label={defineKey('RentRadar')}
      />

      <LinkItem
        track={{ name: 'HousingAnywhere Api' }}
        link={{
          to: urlResolver.getDevelopersUrl(),
          rel,
          target: '_blank',
        }}
        label={defineKey('HousingAnywhere API')}
      />

      {isContentfulSupported(lang) && (
        <LinkItem
          track={{ name: 'Help Center' }}
          link={{
            to: urlResolver.getHelpCenterUrl(lang),
            rel,
          }}
          label="Help Center"
        />
      )}

      <LinkItem
        track={{ name: 'Legal rental information' }}
        link={{
          to: urlResolver.getLegalUrl(),
          rel,
        }}
        label={defineKey('legal_short')}
      />
    </Grid>
  );
};

export { LandlordsLinks };
